import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { MessageSquare, ArrowRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { Button } from "@/components/ui/button";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  actie_luijten: { label: "Actie Luijten", variant: "default" },
  actie_decoprof: { label: "Actie Decoprof", variant: "secondary" },
  afwachting_voorraad: { label: "Voorraad", variant: "outline" },
  afwachting_klant: { label: "Klant", variant: "outline" },
  afwachting_voorraad_ridderkerk: { label: "Ridderkerk", variant: "outline" },
  actie_winkel_ridderkerk: { label: "Winkel RDK", variant: "secondary" },
  afwijkende_verzenddatum: { label: "Verzenddatum", variant: "outline" },
};

const DashboardRecentNotifications = () => {
  const { data: recent } = useQuery({
    queryKey: ["dashboard-recent-notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, vo_number, status, priority, user_name, created_at, type")
        .neq("status", "afgerond")
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return data;
    },
    refetchInterval: 30000,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          Recente meldingen
        </CardTitle>
        <Link to="/notifications">
          <Button variant="ghost" size="sm" className="text-xs">
            Alles bekijken
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-2">
        {recent?.map((n) => {
          const config = statusConfig[n.status] || { label: n.status, variant: "outline" as const };
          return (
            <Link
              key={n.id}
              to={`/notifications/${n.id}`}
              className="flex items-center justify-between p-2.5 rounded-lg hover:bg-muted/50 transition-colors group"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {n.vo_number || n.type}
                  </span>
                  {n.priority === "hoog" && (
                    <Badge variant="destructive" className="text-[10px] h-4 px-1">
                      Urgent
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {n.user_name} · {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: nl })}
                </p>
              </div>
              <Badge variant={config.variant} className="text-[10px] ml-2 flex-shrink-0">
                {config.label}
              </Badge>
            </Link>
          );
        })}
        {(!recent || recent.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Geen openstaande meldingen
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardRecentNotifications;
